import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Check, Clock, ExternalLink, Gift, HeartHandshake, MinusCircle, Pencil, Play, PlusCircle, Repeat, Timer } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { SwitchToKidSheet } from "@/components/SwitchUser";
import { Badge, Button, Card, cx, DurationInput, EmptyState, PageLoader, SectionTitle, TextField, Toggle } from "@/components/ui";
import { Avatar, Points, ProgressBar } from "@/components/bits";
import { api } from "@/lib/api";
import { LEDGER_LABELS, timeAgo } from "@/lib/format";
import { useAction, useApprovals, useGoals, useKidSummary, useLedger, useParentResponsibilities, useRewardTypes } from "@/lib/queries";
import { approvalsForKid, setSelectedKid } from "@/lib/selectedKid";
import { useSession } from "@/lib/session";
import { formatDuration } from "@shared/duration";
import type { BankDTO, RewardTypeDTO, TimeSessionDTO } from "@shared/types";
import { formatRemaining, StopTimeButton, useSessionClock } from "@/components/ScreenTime";
import { BonusSheet, DeductSheet, RecoverySheet, RedeemSheet } from "./actions";
import { CompletionItem, DeliverItem, JobRunItem, PitchItem, RedemptionItem } from "./Approvals";
import { KidJobs } from "./KidJobs";

function TimeSheet({ kidId, rt, bank, onClose }: { kidId: string; rt: RewardTypeDTO; bank: BankDTO; onClose: () => void }) {
  const [mode, setMode] = useState<"grant" | "use">("grant");
  const [minutes, setMinutes] = useState(30);
  const [override, setOverride] = useState(false);
  const [note, setNote] = useState("");
  const act = useAction(
    () =>
      mode === "grant"
        ? api.post(`/rewards/${rt.id}/adjust`, { kidId, units: minutes, note: note || undefined })
        : api.post(`/rewards/${rt.id}/use`, { kidId, units: minutes, override, note: note || undefined }),
    { success: mode === "grant" ? "Time added" : "Session logged", onSuccess: onClose },
  );
  return (
    <Sheet
      open
      onClose={onClose}
      title={rt.name}
      footer={
        <Button block onClick={() => act.mutate()} loading={act.isPending} disabled={minutes <= 0}>
          {mode === "grant" ? `Add ${formatDuration(minutes)}` : `Start ${formatDuration(minutes)} session`}
        </Button>
      }
    >
      <div className="mb-4 grid grid-cols-2 gap-2">
        <Button variant={mode === "grant" ? "primary" : "secondary"} onClick={() => setMode("grant")}>
          Add bonus time
        </Button>
        <Button variant={mode === "use" ? "primary" : "secondary"} onClick={() => setMode("use")}>
          Log a session
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted">
        {mode === "grant"
          ? "Adds to their saved time without spending points."
          : `Uses saved time (${formatDuration(bank.balance)} available). Caps and schedule apply unless you override.`}
      </p>
      <DurationInput value={minutes} onChange={setMinutes} min={5} max={10 * 60} step={5} />
      <div className="mt-4 space-y-4">
        {mode === "use" && <Toggle checked={override} onChange={setOverride} label="Override limits" description="Ignore weekly/daily caps and schedule rules this once." />}
        <TextField label="Note (optional)" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Sheet>
  );
}

/** A kid's countdown as parents see it, with a button to end it (the unused minutes are saved). */
function RunningSession({ session, kidId, receivedAt }: { session: TimeSessionDTO; kidId: string; receivedAt: number }) {
  const left = useSessionClock(session, receivedAt);
  if (left === null || left <= 0) return null;
  return (
    <div className="mt-3 flex items-center gap-3 rounded-2xl bg-coin-soft px-3 py-2.5 text-coin-ink">
      <Timer className="size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-extrabold">Running now · {formatRemaining(left)} left</div>
        <div className="text-xs font-bold">
          Started {new Date(session.startedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {formatDuration(session.minutes)}
        </div>
      </div>
      <StopTimeButton session={session} kidId={kidId} label="End now" />
    </div>
  );
}

/** A big, thumb-sized button for the per-kid actions at the top of a kid's page. */
function QuickAction({
  icon,
  label,
  tone,
  onClick,
  loading,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  tone: "coin" | "danger" | "brand" | "violet";
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}) {
  const tones = {
    coin: "bg-coin-soft text-coin-ink",
    danger: "bg-danger-soft text-danger",
    brand: "bg-brand-soft text-brand-strong",
    violet: "bg-violet-soft text-violet",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={cx(
        "flex min-h-16 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2.5 text-sm font-extrabold transition active:scale-[0.98] disabled:opacity-50",
        tones[tone],
      )}
    >
      <span className="[&>svg]:size-6">{icon}</span>
      <span className="leading-tight">{label}</span>
    </button>
  );
}

/** One kid's page: what needs the parent and the everyday actions first, then the details. */
export function KidView({ kidId }: { kidId: string }) {
  const { family } = useSession();
  const summary = useKidSummary(kidId);
  const ledger = useLedger(kidId, 60);
  const goals = useGoals(kidId);
  const board = useParentResponsibilities();
  const rewards = useRewardTypes();
  const approvals = useApprovals();
  const [sheet, setSheet] = useState<"bonus" | "deduct" | "recovery" | "redeem" | null>(null);
  const [timeSheet, setTimeSheet] = useState<{ rt: RewardTypeDTO; bank: BankDTO } | null>(null);
  const [handing, setHanding] = useState(false);
  const toggle = useAction(({ id, done }: { id: string; done: boolean }) =>
    api.post(`/responsibilities/${id}/${done ? "uncomplete" : "complete"}`, { kidId }),
  );
  const start = useAction((rt: RewardTypeDTO) => api.post<TimeSessionDTO>(`/rewards/${rt.id}/start`, { kidId }), {
    success: (r) => `${formatDuration(r.minutes)} of screen time started`,
  });
  const stop = useAction(
    (session: TimeSessionDTO) => api.post<{ savedMinutes: number }>(`/rewards/${session.rewardTypeId}/stop`, { kidId }),
    { success: (r) => (r.savedMinutes > 0 ? `${formatDuration(r.savedMinutes)} saved for later` : "Screen time stopped") },
  );

  if (summary.isLoading || !summary.data) return <PageLoader />;
  const s = summary.data;
  const kid = s.kid;
  const items = board.data?.board[kid.id]?.items.filter((i) => i.due) ?? [];
  const todo = approvals.data ? approvalsForKid(approvals.data, kid.id) : null;
  const typeOf = (b: BankDTO) => rewards.data?.rewardTypes.find((t) => t.id === b.rewardTypeId);
  // The countdown action follows the kid's main time reward (or whichever one is running).
  const timeBank = s.banks.find((b) => b.session) ?? s.banks.find((b) => typeOf(b)?.kind === "time");
  const timeType = timeBank && typeOf(timeBank);
  const running = s.banks.find((b) => b.session)?.session ?? null;

  function screenTime() {
    if (!timeBank || !timeType) return;
    if (running) stop.mutate(running);
    // Nothing saved or a connected device: the sheet adds time or logs a session instead.
    else if (timeBank.balance > 0 && !timeType.integrationId && timeBank.scheduleAllowedNow) start.mutate(timeType);
    else setTimeSheet({ rt: timeType, bank: timeBank });
  }

  return (
    <div>
      <PageHeader
        title={kid.name}
        subtitle={kid.age ? `Age ${kid.age}${kid.interests ? ` · ${kid.interests}` : ""}` : (kid.interests ?? undefined)}
        bell="/parent/notifications"
        actions={
          <>
            <button
              type="button"
              onClick={() => setHanding(true)}
              aria-label={`Hand to ${kid.name}`}
              title={`Hand to ${kid.name}`}
              className="flex size-11 items-center justify-center rounded-full hover:bg-sunken"
            >
              <Repeat className="size-5" />
            </button>
            <Link to="/parent/family" aria-label="Edit profile" className="flex size-11 items-center justify-center rounded-full hover:bg-sunken">
              <Pencil className="size-5" />
            </Link>
          </>
        }
      />
      <SwitchToKidSheet open={handing} onClose={() => setHanding(false)} kidId={kid.id} />

      <Card className="mt-3">
        <div className="flex items-center gap-4">
          <Avatar member={kid} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-muted">Balance</div>
            <Points value={s.balance} family={family} className={cx("text-4xl", s.balance < 0 ? "text-danger" : "text-coin-ink")} />
          </div>
          <div className="text-right text-xs font-bold text-muted">
            <div>{s.available.toLocaleString()} available</div>
            <div className="text-brand">+{s.earnedThisWeek} this week</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-4 gap-2" role="group" aria-label={`Actions for ${kid.name}`}>
          <QuickAction tone="coin" icon={<PlusCircle />} label="Bonus" onClick={() => setSheet("bonus")} />
          <QuickAction tone="danger" icon={<MinusCircle />} label="Deduct" onClick={() => setSheet("deduct")} />
          <QuickAction
            tone="brand"
            icon={running ? <Timer /> : <Play />}
            label={running ? "Stop time" : timeBank && timeBank.balance > 0 ? "Start time" : "Add time"}
            onClick={screenTime}
            loading={start.isPending || stop.isPending}
            disabled={!timeBank || !timeType}
          />
          <QuickAction tone="violet" icon={<Gift />} label="Redeem" onClick={() => setSheet("redeem")} />
        </div>
        {s.balance < 0 && family.workItOffEnabled && (
          <Button variant="soft" block className="mt-2" onClick={() => setSheet("recovery")}>
            <HeartHandshake className="size-4" /> Work it off
          </Button>
        )}
        {running && <RunningSession session={running} kidId={kid.id} receivedAt={summary.dataUpdatedAt} />}
        {timeBank && timeType && !running && (
          <p className="mt-3 text-center text-xs font-bold text-muted">
            {timeType.icon} {formatDuration(timeBank.balance)} saved
            {!timeBank.scheduleAllowedNow && timeBank.scheduleReason ? ` · ${timeBank.scheduleReason}` : ""}
          </p>
        )}
      </Card>

      {todo && (todo.count > 0 || todo.toDeliver.length > 0) && (
        <>
          <SectionTitle>Needs you ({todo.count + todo.toDeliver.length})</SectionTitle>
          <div className="space-y-3">
            {todo.jobRuns.map((r) => (
              <JobRunItem key={r.id} run={r} />
            ))}
            {todo.completions.map((c) => (
              <CompletionItem key={c.id} c={c} />
            ))}
            {todo.redemptions.map((r) => (
              <RedemptionItem key={r.id} r={r} />
            ))}
            {todo.pitches.map((j) => (
              <PitchItem key={j.id} job={j} />
            ))}
            {todo.toDeliver.map((r) => (
              <DeliverItem key={r.id} r={r} />
            ))}
          </div>
        </>
      )}

      <SectionTitle>Today's responsibilities</SectionTitle>
      <Card>
        {items.length === 0 ? (
          <p className="text-sm text-muted">Nothing due today.</p>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between text-sm font-bold">
              <span>
                {s.gate.done}/{s.gate.total} done
              </span>
              <Badge tone={s.gate.unlocked ? "brand" : "locked"}>{s.gate.unlocked ? "Jobs unlocked" : "Jobs locked"}</Badge>
            </div>
            <div className="space-y-1.5">
              {items.map((i) => {
                const st = i.completion?.status;
                const done = st === "done" || st === "approved";
                return (
                  <button
                    key={i.responsibility.id}
                    type="button"
                    aria-pressed={done}
                    onClick={() => toggle.mutate({ id: i.responsibility.id, done })}
                    className={cx("flex min-h-11 w-full items-center gap-3 rounded-2xl px-3 py-2 text-left", done ? "bg-brand-soft" : "bg-sunken")}
                  >
                    <span className={cx("flex size-7 items-center justify-center rounded-lg", done ? "bg-brand text-on-brand" : "bg-card ring-2 ring-line")}>
                      {done && <Check className="size-4" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 font-bold">
                      {i.responsibility.icon} {i.responsibility.title}
                    </span>
                    {st === "done" && !i.counts && <Badge tone="warn">Needs check</Badge>}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-muted">Tap to check off on {kid.name}'s behalf.</p>
          </>
        )}
      </Card>

      <KidJobs kid={kid} />

      {s.banks.length > 0 && (
        <>
          <SectionTitle>Time rewards</SectionTitle>
          <div className="space-y-2">
            {s.banks.map((b) => {
              const rt = typeOf(b);
              if (!rt) return null;
              return (
                <Card key={b.rewardTypeId}>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{rt.icon}</span>
                    <div className="flex-1">
                      <div className="font-extrabold">{rt.name}</div>
                      <div className="text-sm text-muted">
                        {formatDuration(b.balance)} saved · {formatDuration(b.usedToday)} used today
                      </div>
                    </div>
                    <Button size="sm" variant="secondary" className="h-11" onClick={() => setTimeSheet({ rt, bank: b })}>
                      <Timer className="size-4" /> Manage
                    </Button>
                  </div>
                  {b.weeklyCap !== null && (
                    <div className="mt-3">
                      <div className="mb-1 flex justify-between text-xs font-bold text-muted">
                        <span>Used this week</span>
                        <span>
                          {formatDuration(b.usedThisWeek)} / {formatDuration(b.weeklyCap)}
                        </span>
                      </div>
                      <ProgressBar value={b.usedThisWeek / Math.max(1, b.weeklyCap)} color={b.usedThisWeek >= b.weeklyCap ? "var(--danger)" : "var(--brand)"} />
                    </div>
                  )}
                  {!b.scheduleAllowedNow && (
                    <p className="mt-2 flex items-center gap-1 text-xs font-bold text-warn">
                      <Clock className="size-3.5" /> Right now: {b.scheduleReason}
                    </p>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      <SectionTitle>Goals</SectionTitle>
      {!goals.data || goals.data.goals.length === 0 ? (
        <p className="px-1 text-sm text-muted">No savings goals yet.</p>
      ) : (
        <div className="space-y-2">
          {goals.data.goals.map((g) => (
            <Card key={g.id} className="p-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{g.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 font-extrabold">
                    <span className="truncate">{g.name}</span>
                    {g.url && (
                      <a href={g.url} target="_blank" rel="noreferrer noopener" aria-label="Open link" className="text-faint hover:text-brand">
                        <ExternalLink className="size-4" />
                      </a>
                    )}
                  </div>
                  <div className="text-xs text-muted">
                    <Points value={g.price} family={family} /> {g.priceCash ? `· $${g.priceCash.toFixed(2)}` : ""} · {g.status}
                  </div>
                </div>
                <span className="text-sm font-black">{Math.round(g.progress * 100)}%</span>
              </div>
              <ProgressBar value={g.progress} className="mt-2 h-2" color="var(--coin)" />
            </Card>
          ))}
        </div>
      )}

      <SectionTitle>History</SectionTitle>
      {!ledger.data || ledger.data.entries.length === 0 ? (
        <EmptyState icon="🧾" title="No history yet" />
      ) : (
        <Card className="divide-y divide-line p-0">
          {ledger.data.entries.map((e) => (
            <div key={e.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold">{e.reason || LEDGER_LABELS[e.kind]}</div>
                <div className="truncate text-xs text-muted">
                  {LEDGER_LABELS[e.kind]} · {timeAgo(e.createdAt)}
                  {e.note ? ` · ${e.note}` : ""}
                </div>
              </div>
              <div className="text-right">
                <Points value={e.amount} family={family} signed className={e.amount >= 0 ? "text-brand" : "text-danger"} />
                <div className="text-[11px] font-bold text-faint">bal {e.balanceAfter}</div>
              </div>
            </div>
          ))}
        </Card>
      )}

      {sheet === "bonus" && <BonusSheet summary={s} onClose={() => setSheet(null)} />}
      {sheet === "deduct" && <DeductSheet summary={s} onClose={() => setSheet(null)} />}
      {sheet === "recovery" && <RecoverySheet summary={s} onClose={() => setSheet(null)} />}
      {sheet === "redeem" && <RedeemSheet summary={s} onClose={() => setSheet(null)} />}
      {timeSheet && <TimeSheet kidId={kid.id} rt={timeSheet.rt} bank={timeSheet.bank} onClose={() => setTimeSheet(null)} />}
    </div>
  );
}

/**
 * Old kid links (notifications, the family cards) point here: select that kid on this device and
 * show their page at Home, where the kid switcher keeps them.
 */
export function KidRoute() {
  const { kidId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    if (kidId) setSelectedKid(kidId);
    navigate("/parent", { replace: true });
  }, [kidId, navigate]);
  return <PageLoader />;
}
